import { useState } from 'react'
import { api, errorMessage, useQuery } from '../api.js'
import { Alert, Button, Field, Input, PageHeader, Panel, Pill } from '../components/ui.js'
import { StepUpModal, type StepUpRequest } from '../components/StepUpModal.js'
import { PendentesDeAprovacao } from '../components/ConectarServidor.js'

type Conta = Awaited<ReturnType<typeof api.identity.accounts.query>>[number]
type Servidor = Awaited<ReturnType<typeof api.fleet.servers.query>>[number]

/**
 * Contas de sistema operacional.
 *
 * Duas visoes, porque sao duas perguntas diferentes: "quem entra NESTE
 * servidor" (operacao do dia) e "o que ESTA PESSOA acessa" (a que importa no
 * dia do desligamento, e que so tem resposta rapida se a plataforma juntar os
 * hosts).
 */
export function Accounts() {
  const [visao, setVisao] = useState<'servidor' | 'pessoa'>('servidor')
  const [servidorId, setServidorId] = useState<string | null>(null)
  const [stepUp, setStepUp] = useState<StepUpRequest | null>(null)
  const [senhaRevelada, setSenhaRevelada] = useState<{ label: string; valor: string } | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  // Todas com polling. A causa mais provavel do "select de servidor vazio" era
  // esta lista de servidores buscada UMA vez (deps []): se aquele unico fetch
  // caisse — tipico logo apos um deploy, com a API reiniciando — nunca mais
  // tentava, e a tela ficava presa sem servidor, sem poder criar conta. A aba
  // Frota, que usa a mesma consulta COM polling, funcionava; a diferenca era so
  // essa. Agora as duas se recuperam sozinhas.
  const frota = useQuery(() => api.fleet.servers.query(), [], { pollMs: 15_000 })
  const agentes = useQuery(() => api.identity.agents.query(), [], { pollMs: 10_000 })
  const contas = useQuery(() => api.identity.accounts.query(), [visao], { pollMs: 20_000 })
  const pessoas = useQuery(() => api.identity.people.query(), [], { pollMs: 30_000 })
  const postura = useQuery(() => api.identity.posture.query(), [], { pollMs: 30_000 })

  const servidores = frota.data ?? []
  const alvo = servidorId ?? servidores[0]?.id ?? null
  const agentePorServidor = new Map((agentes.data ?? []).map((a) => [a.serverId, a]))

  async function recarregar() {
    await Promise.all([contas.refetch(), postura.refetch(), agentes.refetch(), pessoas.refetch()])
  }

  async function comErro(fn: () => Promise<unknown>) {
    setOcupado(true)
    setErro(null)
    try {
      await fn()
      await recarregar()
    } catch (err) {
      setErro(errorMessage(err))
    } finally {
      setOcupado(false)
    }
  }

  /** Abre o link de senha uma unica vez — depois disso ele nao existe mais. */
  async function revelar(linkId: string) {
    try {
      setSenhaRevelada(await api.identity.readSecretLink.mutate({ linkId }))
    } catch (err) {
      setErro(errorMessage(err))
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Contas"
        subtitle="Quem entra em cada servidor e o que cada pessoa acessa. Aprove servidores novos, crie usuários e administre acesso."
      />

      {erro && <Alert>{erro}</Alert>}

      {senhaRevelada && (
        <SenhaUnica dados={senhaRevelada} onClose={() => setSenhaRevelada(null)} />
      )}

      <PendentesDeAprovacao />

      <div className="flex items-center gap-2">
        {(
          [
            ['servidor', 'Por servidor'],
            ['pessoa', 'Por pessoa'],
          ] as const
        ).map(([k, rotulo]) => (
          <button
            key={k}
            onClick={() => setVisao(k)}
            className={`rounded px-3 py-1.5 text-sm ${
              visao === k ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:text-ink'
            }`}
          >
            {rotulo}
          </button>
        ))}
      </div>

      <Postura avisos={postura.data ?? []} />

      {visao === 'servidor' ? (
        <PorServidor
          servidores={servidores}
          carregando={frota.loading && !frota.data}
          erroFrota={frota.error}
          alvo={alvo}
          onEscolher={setServidorId}
          contas={(contas.data ?? []).filter((c) => c.serverId === alvo)}
          agente={alvo ? agentePorServidor.get(alvo) : undefined}
          pessoas={pessoas.data ?? []}
          ocupado={ocupado}
          onStepUp={setStepUp}
          onAcao={comErro}
          onRevelar={revelar}
        />
      ) : (
        <PorPessoa pessoas={pessoas.data ?? []} contas={contas.data ?? []} onAcao={comErro} />
      )}

      {stepUp && <StepUpModal request={stepUp} onClose={() => setStepUp(null)} />}
    </div>
  )
}

function Postura({ avisos }: { avisos: Array<{ nivel: string; username: string; detalhe: string }> }) {
  if (!avisos.length) return null
  const criticos = avisos.filter((a) => a.nivel === 'critico')
  const outros = avisos.filter((a) => a.nivel !== 'critico')

  return (
    <Panel title={`Postura — ${criticos.length} critico(s), ${outros.length} aviso(s)`}>
      <ul className="space-y-2 text-sm">
        {[...criticos, ...outros].slice(0, 8).map((a, i) => (
          <li key={i} className="flex items-start gap-3">
            <Pill tone={a.nivel === 'critico' ? 'crit' : 'warn'}>{a.nivel}</Pill>
            <span className="text-ink-2">
              <span className="font-mono text-ink">{a.username}</span> — {a.detalhe}
            </span>
          </li>
        ))}
      </ul>
      {avisos.length > 8 && (
        <p className="mt-3 text-xs text-ink-3">e mais {avisos.length - 8} item(ns).</p>
      )}
    </Panel>
  )
}

function PorServidor({
  servidores,
  carregando,
  erroFrota,
  alvo,
  onEscolher,
  contas,
  agente,
  pessoas,
  ocupado,
  onStepUp,
  onAcao,
  onRevelar,
}: {
  servidores: Servidor[]
  carregando: boolean
  erroFrota: string | null
  alvo: string | null
  onEscolher: (id: string) => void
  contas: Conta[]
  agente?: { status: string; hostname: string | null; version: string | null }
  pessoas: Array<{ id: string; name: string }>
  ocupado: boolean
  onStepUp: (r: StepUpRequest) => void
  onAcao: (fn: () => Promise<unknown>) => Promise<void>
  onRevelar: (linkId: string) => Promise<void>
}) {
  const [criando, setCriando] = useState(false)
  const servidor = servidores.find((s) => s.id === alvo)
  const online = agente?.status === 'online'
  const semServidor = servidores.length === 0

  // Uma unica leitura dos grupos do host, compartilhada: o painel "Grupos no
  // host" edita, e o detalhe de cada conta mostra de quais grupos ela participa.
  const grupos = useQuery(
    () => (alvo ? api.identity.groups.query({ serverId: alvo }) : Promise.resolve([])),
    [alvo],
  )
  const todosGrupos = grupos.data ?? []

  return (
    <div className="space-y-6">
      <Panel
        title="Servidor"
        action={
          <div className="flex items-center gap-2">
            {agente ? (
              <Pill tone={online ? 'ok' : 'warn'}>
                agente {online ? 'online' : agente.status}
              </Pill>
            ) : (
              <Pill tone="neutral">sem agente</Pill>
            )}
            <Button
              onClick={() => alvo && onAcao(() => api.identity.syncAccounts.mutate({ serverId: alvo }))}
              disabled={!online || ocupado}
              title={online ? undefined : 'Precisa de um agente online neste servidor'}
            >
              Ler do host
            </Button>
            <Button
              variant="primary"
              onClick={() => setCriando(true)}
              disabled={!online || ocupado}
              title={online ? undefined : 'Precisa de um agente online neste servidor'}
            >
              Nova conta
            </Button>
          </div>
        }
      >
        {semServidor ? (
          <div className="text-sm text-ink-2">
            {erroFrota ? (
              <Alert>
                Nao consegui carregar os servidores: {erroFrota}. O painel tenta de novo sozinho a
                cada 15 segundos.
              </Alert>
            ) : carregando ? (
              'Carregando servidores...'
            ) : (
              <>
                Nenhum servidor aqui ainda. Cadastre um provedor e sincronize, ou adicione um
                servidor na aba <span className="font-medium text-ink">Frota</span>. Se voce ja tem
                servidores la, aguarde um instante — esta lista se atualiza sozinha.
              </>
            )}
          </div>
        ) : (
          <>
            <select
              value={alvo ?? ''}
              onChange={(e) => onEscolher(e.target.value)}
              className="w-full rounded border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
            >
              {servidores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.hostname} {s.publicIp ? `— ${s.publicIp}` : ''}
                </option>
              ))}
            </select>

            {!online && servidor && (
              <InstalarAgente servidor={servidor} jaInscrito={Boolean(agente)} />
            )}
          </>
        )}
      </Panel>

      {criando && servidor && (
        <NovaConta
          servidor={servidor}
          pessoas={pessoas}
          onStepUp={onStepUp}
          onFeito={onRevelar}
          onClose={() => setCriando(false)}
        />
      )}

      <Grupos
        serverId={alvo}
        contas={contas}
        grupos={grupos}
        podeAgir={online && !ocupado}
        onStepUp={onStepUp}
      />

      <Panel title={`Contas — ${contas.length}`}>
        {contas.length === 0 ? (
          <p className="text-sm text-ink-3">
            Nenhuma conta lida ainda. Use "Ler do host" para trazer o que existe na maquina.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-wider text-ink-3">
                <th className="pb-2">Conta</th>
                <th className="pb-2">Estado</th>
                <th className="pb-2">Pessoa</th>
                <th className="pb-2 text-right">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {contas.map((c) => (
                <LinhaDaConta
                  key={c.id}
                  conta={c}
                  servidor={servidor}
                  pessoas={pessoas}
                  gruposDaConta={todosGrupos
                    .filter((g) => g.members.includes(c.username))
                    .map((g) => g.group)}
                  podeAgir={online && !ocupado}
                  onStepUp={onStepUp}
                  onAcao={onAcao}
                  onRevelar={onRevelar}
                />
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}

/**
 * Grupos locais do host, com entrada e saida de membro.
 *
 * Administrador e area de trabalho remota tem botao proprio na linha da conta,
 * porque sao resolvidos por SID — o nome deles muda com o idioma do Windows.
 * Este painel e para o resto: grupo de aplicacao, grupo criado pelo cliente.
 */
type GruposQuery = {
  data: Array<{ group: string; members: string[] }> | null
  refetch: () => Promise<void>
}

function Grupos({
  serverId,
  contas,
  grupos,
  podeAgir,
  onStepUp,
}: {
  serverId: string | null
  contas: Conta[]
  grupos: GruposQuery
  podeAgir: boolean
  onStepUp: (r: StepUpRequest) => void
}) {
  const [grupo, setGrupo] = useState('')
  const [usuario, setUsuario] = useState('')

  const lista = grupos.data ?? []
  const podeIncluir = grupo.trim().length > 0 && usuario.length > 0

  return (
    <Panel title="Grupos no host">
      {lista.length === 0 ? (
        <p className="text-sm text-ink-3">
          Nenhum grupo lido ainda — use "Ler do host". Grupo sem nenhum membro nao aparece aqui:
          a tabela guarda uma linha por membro. Quem responde "tem acesso remoto" e a marca na
          linha da conta.
        </p>
      ) : (
        <ul className="space-y-3">
          {lista.map((g) => (
            <li key={g.group}>
              <div className="font-mono text-[11px] uppercase tracking-wider text-ink-3">
                {g.group}
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {g.members.map((m) => (
                  <span
                    key={m}
                    className="inline-flex items-center gap-1.5 rounded border border-line px-2 py-0.5 text-xs text-ink-2"
                  >
                    <span className="font-mono">{m}</span>
                    <button
                      disabled={!podeAgir}
                      title={`Tirar ${m} de ${g.group}`}
                      onClick={() =>
                        onStepUp({
                          action: 'os.group.change',
                          title: `Tirar ${m} de ${g.group}`,
                          description: 'A conta sai deste grupo local e perde o que ele concedia.',
                          onProved: async (proofId) => {
                            await api.identity.changeGroup.mutate({
                              stepUpProofId: proofId,
                              serverId: serverId!,
                              username: m,
                              group: g.group,
                              adicionar: false,
                            })
                            await grupos.refetch()
                          },
                        })
                      }
                      className="text-ink-3 hover:text-crit disabled:opacity-30"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 flex flex-wrap items-end gap-2 border-t border-line pt-4">
        <Field label="Conta">
          <select
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            className="rounded border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
          >
            <option value="">escolha</option>
            {contas.map((c) => (
              <option key={c.id} value={c.username}>
                {c.username}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Grupo" hint="O nome exato como aparece no host.">
          <Input
            value={grupo}
            onChange={(e) => setGrupo(e.target.value)}
            placeholder="Operadores de copia"
          />
        </Field>
        <Button
          disabled={!podeIncluir || !podeAgir}
          onClick={() =>
            onStepUp({
              action: 'os.group.change',
              title: `Incluir ${usuario} em ${grupo}`,
              description:
                'A conta entra neste grupo local e passa a ter o que ele concede. O painel nao sabe o que cada grupo permite — confira no host antes.',
              onProved: async (proofId) => {
                await api.identity.changeGroup.mutate({
                  stepUpProofId: proofId,
                  serverId: serverId!,
                  username: usuario,
                  group: grupo.trim(),
                  adicionar: true,
                })
                setGrupo('')
                await grupos.refetch()
              },
            })
          }
        >
          Incluir no grupo
        </Button>
      </div>
    </Panel>
  )
}

/** Monta o instalador do Windows (.ps1) com token e URL embutidos. */
function scriptWindows(painel: string, token: string): string {
  return [
    '#Requires -RunAsAdministrator',
    "$ErrorActionPreference = 'Stop'",
    `$painel = '${painel}'`,
    `$token  = '${token}'`,
    "$dir = Join-Path $env:ProgramData 'Via360'",
    'New-Item -ItemType Directory -Force -Path $dir | Out-Null',
    "$bin = Join-Path $dir 'via-agent.exe'",
    "Write-Host 'Baixando o agente...'",
    'Invoke-WebRequest -Uri "$painel/agent/download/windows" -OutFile $bin',
    "Write-Host 'Inscrevendo este host...'",
    '& $bin inscrever -painel $painel -token $token',
    "Write-Host 'Instalando o servico...'",
    '& $bin instalar',
    "Write-Host 'Pronto. O agente esta rodando como servico e sobe sozinho no boot.'",
    '',
  ].join('\r\n')
}

/** Monta o instalador do Linux (.sh) com token, URL e a unit systemd. */
function scriptLinux(painel: string, token: string): string {
  return [
    '#!/usr/bin/env bash',
    'set -e',
    '[ "$(id -u)" = 0 ] || { echo "rode como root (sudo)"; exit 1; }',
    `painel='${painel}'`,
    `token='${token}'`,
    'echo "Baixando o agente..."',
    'curl -fsSL "$painel/agent/download/linux" -o /usr/local/bin/via-agent',
    'chmod +x /usr/local/bin/via-agent',
    'echo "Inscrevendo este host..."',
    '/usr/local/bin/via-agent inscrever -painel "$painel" -token "$token"',
    'cat > /etc/systemd/system/via-agent.service <<UNIT',
    '[Unit]',
    'Description=Via 360 Graus - agente',
    'After=network-online.target',
    '[Service]',
    'ExecStart=/usr/local/bin/via-agent rodar',
    'User=root',
    'Restart=always',
    'RestartSec=5',
    '[Install]',
    'WantedBy=multi-user.target',
    'UNIT',
    'systemctl daemon-reload',
    'systemctl enable --now via-agent',
    'echo "Pronto. O agente esta rodando como servico."',
    '',
  ].join('\n')
}

/**
 * Instalar o agente num servidor que ainda nao tem (ou esta offline).
 *
 * "Um clique" de verdade: o painel gera o instalador ja com o token embutido e
 * o entrega como arquivo. Voce baixa, roda uma vez como administrador, e ele
 * baixa o binario, inscreve o host e se registra como servico. Nao ha token
 * para copiar nem comando para montar — a fricom que motivou este pedido.
 *
 * O token e de uso unico e vale 30 min; o painel guarda so o hash. Ele vive
 * dentro do arquivo baixado e some quando voce o apaga.
 */
/**
 * Comando de UMA LINHA para colar num PowerShell Admin.
 *
 * E o caminho a prova de falha, e a razao existe: um `.ps1` baixado da
 * internet quase nunca roda com duplo-clique — o Windows abre no editor — nem
 * com "Executar com PowerShell", porque a politica de execucao e a marca de
 * arquivo-baixado bloqueiam. Um comando COLADO num prompt interativo nao sofre
 * disso: politica de execucao so vale para arquivos .ps1, nao para o que voce
 * digita. Baixa o binario, inscreve e instala o servico numa tacada.
 */
function comandoWindows(painel: string, token: string): string {
  return (
    `$p='${painel}';$t='${token}';` +
    `$d="$env:ProgramData\\Via360";New-Item -ItemType Directory -Force $d|Out-Null;` +
    `$b="$d\\via-agent.exe";Invoke-WebRequest "$p/agent/download/windows" -OutFile $b;` +
    `& $b inscrever -painel $p -token $t;& $b instalar`
  )
}

/** Comando de uma linha para colar num shell root (Linux). */
function comandoLinux(painel: string, token: string): string {
  return `curl -fsSL '${painel}/agent/download/linux' -o /usr/local/bin/via-agent && chmod +x /usr/local/bin/via-agent && /usr/local/bin/via-agent inscrever -painel '${painel}' -token '${token}' && printf '[Unit]\\nAfter=network-online.target\\n[Service]\\nExecStart=/usr/local/bin/via-agent rodar\\nUser=root\\nRestart=always\\n[Install]\\nWantedBy=multi-user.target\\n' > /etc/systemd/system/via-agent.service && systemctl enable --now via-agent`
}

function InstalarAgente({
  servidor,
  jaInscrito,
}: {
  servidor: Servidor
  jaInscrito: boolean
}) {
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [comando, setComando] = useState<string | null>(null)
  const [copiado, setCopiado] = useState(false)
  const [dados, setDados] = useState<{ token: string; publicUrl: string } | null>(null)

  // A API da Kamatera nao informa o SO; na duvida, oferece Windows (o caso da
  // frota atual).
  const [alvo, setAlvo] = useState<'windows' | 'linux'>(servidor.os === 'linux' ? 'linux' : 'windows')

  async function gerar() {
    setOcupado(true)
    setErro(null)
    try {
      const r = await api.identity.issueEnrollToken.mutate({ serverId: servidor.id })
      setDados({ token: r.token, publicUrl: r.publicUrl })
      setComando(alvo === 'windows' ? comandoWindows(r.publicUrl, r.token) : comandoLinux(r.publicUrl, r.token))
      setCopiado(false)
    } catch (e) {
      setErro(errorMessage(e))
    } finally {
      setOcupado(false)
    }
  }

  function baixarPs1() {
    if (!dados) return
    const conteudo = alvo === 'windows' ? scriptWindows(dados.publicUrl, dados.token) : scriptLinux(dados.publicUrl, dados.token)
    const ext = alvo === 'windows' ? 'ps1' : 'sh'
    const blob = new Blob([conteudo], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `via360-instalar-${servidor.hostname}.${ext}`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mt-4 rounded border border-line bg-ground p-4">
      <p className="text-sm text-ink-2">
        {jaInscrito
          ? 'O agente deste servidor esta offline. Gere o comando de novo para reinstalar.'
          : 'Este servidor ainda nao tem agente. Sem ele o painel enxerga a maquina pela API do provedor, mas nao consegue ler nem criar conta nenhuma.'}
      </p>

      {erro && <div className="mt-3"><Alert>{erro}</Alert></div>}

      {!comando ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={alvo}
            onChange={(e) => setAlvo(e.target.value as 'windows' | 'linux')}
            className="rounded border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
          >
            <option value="windows">Windows</option>
            <option value="linux">Linux</option>
          </select>
          <Button variant="primary" onClick={gerar} disabled={ocupado}>
            {ocupado ? 'Gerando...' : 'Gerar comando de instalacao'}
          </Button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <ol className="space-y-2 text-sm text-ink-2">
            <li>
              <span className="font-medium text-ink">1.</span> No servidor{' '}
              <span className="font-mono text-ink">{servidor.hostname}</span>, abra o{' '}
              {alvo === 'windows' ? (
                <>
                  <span className="font-medium">PowerShell como Administrador</span> (botao direito no
                  menu Iniciar → <span className="font-mono text-xs">Terminal (Admin)</span>).
                </>
              ) : (
                <>terminal como <span className="font-mono text-xs">root</span> (ou use sudo).</>
              )}
            </li>
            <li>
              <span className="font-medium text-ink">2.</span> Cole a linha abaixo e tecle Enter:
            </li>
          </ol>

          <div className="relative">
            <pre className="max-h-32 select-all overflow-auto rounded border border-line bg-panel px-3 py-2 pr-20 font-mono text-[11px] leading-relaxed text-ink">
{comando}
            </pre>
            <button
              onClick={() => {
                void navigator.clipboard?.writeText(comando)
                setCopiado(true)
              }}
              className="absolute right-2 top-2 rounded border border-line bg-ground px-2 py-1 text-xs text-ink-2 hover:border-accent hover:text-accent"
            >
              {copiado ? 'copiado' : 'copiar'}
            </button>
          </div>

          <p className="text-xs text-ink-3">
            O token vale 30 minutos e serve uma vez. Em segundos o agente aparece como online aqui e
            o botao “Nova conta” habilita.{' '}
            <button onClick={baixarPs1} className="underline underline-offset-2 hover:text-accent">
              Prefere um arquivo? Baixar o instalador.
            </button>
          </p>
        </div>
      )}
    </div>
  )
}

function quando(d: string | Date | null | undefined): string {
  if (!d) return '—'
  const dt = new Date(d)
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleString('pt-BR')
}

function LinhaDaConta({
  conta,
  servidor,
  pessoas,
  gruposDaConta,
  podeAgir,
  onStepUp,
  onAcao,
  onRevelar,
}: {
  conta: Conta
  servidor?: Servidor
  pessoas: Array<{ id: string; name: string }>
  gruposDaConta: string[]
  podeAgir: boolean
  onStepUp: (r: StepUpRequest) => void
  onAcao: (fn: () => Promise<unknown>) => Promise<void>
  onRevelar: (linkId: string) => Promise<void>
}) {
  const alvo = { serverId: conta.serverId, username: conta.username }
  // Ver o detalhe nao depende de agente online: e leitura do ultimo retrato.
  const [aberto, setAberto] = useState(false)

  return (
    <>
    <tr className="border-b border-line/60 last:border-0">
      <td className="py-2.5">
        <button
          onClick={() => setAberto((a) => !a)}
          className="flex items-start gap-1.5 text-left hover:opacity-80"
          title={aberto ? 'Ocultar detalhes' : 'Ver detalhes desta conta'}
        >
          <span className="mt-0.5 w-3 shrink-0 font-mono text-[11px] text-ink-3">
            {aberto ? '▾' : '▸'}
          </span>
          <span>
            <span className="block font-mono text-ink">{conta.username}</span>
            {conta.fullName && <span className="block text-xs text-ink-3">{conta.fullName}</span>}
          </span>
        </button>
      </td>
      <td className="py-2.5">
        <div className="flex flex-wrap gap-1">
          <Pill tone={conta.enabled ? 'ok' : 'neutral'}>{conta.enabled ? 'ativa' : 'desabilitada'}</Pill>
          {conta.isAdmin && <Pill tone="crit">admin</Pill>}
          {conta.canRdp && <Pill tone="accent">rdp</Pill>}
        </div>
      </td>
      <td className="py-2.5">
        <select
          value={conta.person?.id ?? ''}
          onChange={(e) =>
            onAcao(() =>
              api.identity.linkAccount.mutate({
                accountId: conta.id,
                personId: e.target.value || null,
              }),
            )
          }
          className="rounded border border-line bg-panel px-2 py-1 text-xs text-ink focus:border-accent focus:outline-none"
        >
          <option value="">sem pessoa</option>
          {pessoas.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </td>
      <td className="py-2.5">
        <div className="flex justify-end gap-1.5">
          <Button
            variant="ghost"
            disabled={!podeAgir}
            onClick={() =>
              onStepUp({
                action: 'os.password.reset',
                title: `Trocar a senha de ${conta.username}`,
                description:
                  'Uma senha nova e gerada e aplicada no host. Ela aparece uma unica vez, aqui, e nao fica guardada em lugar nenhum.',
                requireReason: true,
                onProved: async (proofId) => {
                  const r = await api.identity.resetPassword.mutate({ stepUpProofId: proofId, ...alvo })
                  await onRevelar(r.linkId)
                },
              })
            }
          >
            senha
          </Button>

          {conta.enabled ? (
            <Button
              variant="ghost"
              disabled={!podeAgir}
              onClick={() =>
                onStepUp({
                  action: 'os.account.disable',
                  title: `Desabilitar ${conta.username}`,
                  description: 'A conta deixa de entrar. Nada e apagado, e da para reabilitar depois.',
                  onProved: async (proofId) => {
                    await api.identity.disableAccount.mutate({ stepUpProofId: proofId, ...alvo })
                  },
                })
              }
            >
              desabilitar
            </Button>
          ) : (
            <Button
              variant="ghost"
              disabled={!podeAgir}
              onClick={() => onAcao(() => api.identity.enableAccount.mutate(alvo))}
            >
              habilitar
            </Button>
          )}

          {conta.isAdmin ? (
            <Button
              variant="ghost"
              disabled={!podeAgir}
              onClick={() =>
                onStepUp({
                  action: 'os.admin.revoke',
                  title: `Tirar administrador de ${conta.username}`,
                  description:
                    'A conta deixa de ser administradora da maquina. Cuidado: rebaixar a conta errada pode trancar voce para fora, e o caminho de volta e o console do provedor.',
                  requireReason: true,
                  onProved: async (proofId) => {
                    await api.identity.revokeAdmin.mutate({ stepUpProofId: proofId, ...alvo })
                  },
                })
              }
            >
              tirar admin
            </Button>
          ) : (
            <Button
              variant="ghost"
              disabled={!podeAgir}
              onClick={() =>
                onStepUp({
                  action: 'os.admin.grant',
                  title: `Tornar ${conta.username} administrador`,
                  description:
                    'A conta passa a poder tudo nesta maquina, inclusive desinstalar o agente. E a concessao de maior alcance que este painel faz.',
                  requireReason: true,
                  onProved: async (proofId) => {
                    await api.identity.grantAdmin.mutate({ stepUpProofId: proofId, ...alvo })
                  },
                })
              }
            >
              dar admin
            </Button>
          )}

          {conta.canRdp ? (
            <Button
              variant="ghost"
              disabled={!podeAgir}
              onClick={() => onAcao(() => api.identity.revokeRdp.mutate(alvo))}
            >
              tirar rdp
            </Button>
          ) : (
            <Button
              variant="ghost"
              disabled={!podeAgir}
              onClick={() =>
                onStepUp({
                  action: 'os.rdp.grant',
                  title: `Dar acesso remoto a ${conta.username}`,
                  description: 'A conta entra no grupo de area de trabalho remota deste servidor.',
                  onProved: async (proofId) => {
                    await api.identity.grantRdp.mutate({ stepUpProofId: proofId, ...alvo })
                  },
                })
              }
            >
              dar rdp
            </Button>
          )}

          <Button
            variant="danger"
            disabled={!podeAgir}
            onClick={() =>
              onStepUp({
                action: 'os.account.delete',
                title: `Apagar ${conta.username}`,
                description:
                  'Apagar destroi o SID da conta: recriar com o mesmo nome NAO recupera o perfil nem as permissoes que dependiam dele. Nao tem volta.',
                confirmHostname: servidor?.hostname,
                requireReason: true,
                onProved: async (proofId) => {
                  await api.identity.deleteAccount.mutate({
                    stepUpProofId: proofId,
                    ...alvo,
                    confirmHostname: servidor?.hostname ?? '',
                  })
                },
              })
            }
          >
            apagar
          </Button>
        </div>
      </td>
    </tr>

    {aberto && (
      <tr className="border-b border-line/60 bg-ground/40 last:border-0">
        <td colSpan={4} className="px-4 py-4">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-3">
                Acesso neste servidor
              </div>
              <ul className="space-y-1 text-xs text-ink-2">
                <li>
                  Administrador da maquina:{' '}
                  <span className={conta.isAdmin ? 'font-medium text-crit' : 'text-ink-3'}>
                    {conta.isAdmin ? 'sim' : 'nao'}
                  </span>
                </li>
                <li>
                  Area de trabalho remota (RDP):{' '}
                  <span className={conta.canRdp ? 'font-medium text-accent' : 'text-ink-3'}>
                    {conta.canRdp ? 'sim' : 'nao'}
                  </span>
                </li>
                <li>
                  Estado:{' '}
                  <span className={conta.enabled ? 'text-ok' : 'text-ink-3'}>
                    {conta.enabled ? 'ativa' : 'desabilitada'}
                  </span>
                </li>
                <li>
                  Grupos locais:{' '}
                  {gruposDaConta.length ? (
                    <span className="font-mono text-ink">{gruposDaConta.join(', ')}</span>
                  ) : (
                    <span className="text-ink-3">nenhum lido — use "Ler do host"</span>
                  )}
                </li>
              </ul>
            </div>
            <div>
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-3">
                Registro
              </div>
              <ul className="space-y-1 text-xs text-ink-2">
                <li>Pessoa: {conta.person?.name ?? <span className="text-ink-3">sem pessoa</span>}</li>
                <li>Nome completo: {conta.fullName ?? '—'}</li>
                <li>Ultimo logon: {quando(conta.lastLogonAt)}</li>
                <li>Senha girada em: {quando(conta.passwordRotatedAt)}</li>
                <li>Visto no host em: {quando(conta.observedAt)}</li>
              </ul>
            </div>
          </div>
        </td>
      </tr>
    )}
    </>
  )
}

function NovaConta({
  servidor,
  pessoas,
  onStepUp,
  onFeito,
  onClose,
}: {
  servidor: Servidor
  pessoas: Array<{ id: string; name: string }>
  onStepUp: (r: StepUpRequest) => void
  onFeito: (linkId: string) => Promise<void>
  onClose: () => void
}) {
  const [username, setUsername] = useState('')
  const [fullName, setFullName] = useState('')
  const [personId, setPersonId] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [canRdp, setCanRdp] = useState(false)

  // Espelha o schema do painel: melhor recusar aqui, com a regra explicada, do
  // que deixar a API devolver erro depois de o usuario preencher tudo.
  const nomeValido = /^[A-Za-z0-9._-]+$/.test(username) && username.length <= 20 && !username.endsWith('.')

  return (
    <Panel title={`Nova conta em ${servidor.hostname}`}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Nome de usuario"
          hint="Ate 20 caracteres: letras sem acento, numeros, ponto, hifen e sublinhado."
        >
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="joao.silva"
            autoComplete="off"
          />
        </Field>
        <Field label="Nome completo">
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Joao Silva" />
        </Field>
        <Field label="Pessoa" hint="Conta sem pessoa vinculada vira aviso na postura.">
          <select
            value={personId}
            onChange={(e) => setPersonId(e.target.value)}
            className="w-full rounded border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
          >
            <option value="">sem pessoa</option>
            {pessoas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="space-y-2 pt-6">
          <label className="flex items-center gap-2 text-sm text-ink-2">
            <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
            Administrador da maquina
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-2">
            <input type="checkbox" checked={canRdp} onChange={(e) => setCanRdp(e.target.checked)} />
            Pode entrar por area de trabalho remota
          </label>
        </div>
      </div>

      {username && !nomeValido && (
        <div className="mt-4">
          <Alert tone="warn">
            Nome invalido. Use ate 20 caracteres, sem espaco e sem acento, e nao termine com ponto.
          </Alert>
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          disabled={!nomeValido}
          onClick={() =>
            onStepUp({
              action: 'os.account.create',
              title: `Criar ${username} em ${servidor.hostname}`,
              description: isAdmin
                ? 'Esta conta nascera com privilegio de administrador da maquina. A senha aparece uma unica vez.'
                : 'A senha e gerada pelo painel e aparece uma unica vez, depois de confirmar.',
              onProved: async (proofId) => {
                const r = await api.identity.createAccount.mutate({
                  stepUpProofId: proofId,
                  serverId: servidor.id,
                  username,
                  fullName: fullName || undefined,
                  isAdmin,
                  canRdp,
                  personId: personId || undefined,
                })
                await onFeito(r.linkId)
                onClose()
              },
            })
          }
        >
          Criar conta
        </Button>
      </div>
    </Panel>
  )
}

function PorPessoa({
  pessoas,
  contas,
  onAcao,
}: {
  pessoas: Array<{ id: string; name: string; email: string | null; status: string; contas: unknown[] }>
  contas: Conta[]
  onAcao: (fn: () => Promise<unknown>) => Promise<void>
}) {
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')

  const semPessoa = contas.filter((c) => !c.person)

  return (
    <div className="space-y-6">
      <Panel title="Nova pessoa">
        <div className="flex gap-3">
          <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome" />
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e-mail (opcional)" />
          <Button
            variant="primary"
            disabled={nome.trim().length < 2}
            onClick={() =>
              onAcao(async () => {
                await api.identity.createPerson.mutate({ name: nome, email: email || undefined })
                setNome('')
                setEmail('')
              })
            }
          >
            Adicionar
          </Button>
        </div>
      </Panel>

      {pessoas.map((p) => {
        const dela = contas.filter((c) => c.person?.id === p.id)
        return (
          <Panel key={p.id} title={p.name}>
            {dela.length === 0 ? (
              <p className="text-sm text-ink-3">Nenhuma conta vinculada.</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {dela.map((c) => (
                  <li key={c.id} className="flex items-center gap-3">
                    <span className="font-mono text-ink">{c.hostname}</span>
                    <span className="text-ink-3">\</span>
                    <span className="font-mono text-ink-2">{c.username}</span>
                    {c.isAdmin && <Pill tone="crit">admin</Pill>}
                    {c.canRdp && <Pill tone="accent">rdp</Pill>}
                    {!c.enabled && <Pill tone="neutral">desabilitada</Pill>}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        )
      })}

      {semPessoa.length > 0 && (
        <Panel title={`Contas sem pessoa — ${semPessoa.length}`}>
          <p className="mb-3 text-sm text-ink-2">
            Estas contas existem nos hosts e ninguem responde por elas. E a lista que sobra quando
            alguem sai e ninguem revoga.
          </p>
          <ul className="space-y-1 text-sm">
            {semPessoa.map((c) => (
              <li key={c.id} className="flex items-center gap-3">
                <span className="font-mono text-ink-2">
                  {c.hostname}\{c.username}
                </span>
                {c.isAdmin && <Pill tone="crit">admin</Pill>}
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  )
}

/**
 * A senha aparece uma vez.
 *
 * Fecha o modal e ela some — nao ha "ver de novo", porque o link ja foi
 * queimado no servidor e o valor apagado do banco. Dizer isso na tela evita a
 * ligacao de dez minutos depois.
 */
function SenhaUnica({ dados, onClose }: { dados: { label: string; valor: string }; onClose: () => void }) {
  const [copiado, setCopiado] = useState(false)
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/45 p-4">
      <div className="w-full max-w-md rounded border border-line bg-panel p-6 shadow-xl">
        <h3 className="text-lg font-semibold tracking-tight">{dados.label}</h3>
        <p className="mt-2 text-sm text-ink-2">
          Copie agora. Esta senha nao esta guardada em lugar nenhum e nao aparece de novo.
        </p>
        <div className="mt-4 select-all break-all rounded border border-line bg-ground px-4 py-3 font-mono text-sm text-ink">
          {dados.valor}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button
            onClick={() => {
              void navigator.clipboard?.writeText(dados.valor)
              setCopiado(true)
            }}
          >
            {copiado ? 'Copiado' : 'Copiar'}
          </Button>
          <Button variant="primary" onClick={onClose}>
            Guardei
          </Button>
        </div>
      </div>
    </div>
  )
}
